import { DataGridColumn, DataType, EntityFilterPropertyModel, EntityMetaDataModelOperator, ItemSource, QueryColumnHelper, QueryFilter } from "../../../models/queries/queries.models";
import { autoinject, bindable, BindingEngine, observable, transient } from "aurelia-framework";
import { GridUtils } from "../data-grid/data-grid";
import { Router } from "aurelia-router";
import { I18N } from "aurelia-i18n";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { Guid } from "../../../utilities/guid";
import { Messages } from "../../../configuration/event-messages.constants";
import { RightShipDialogServiceConstants } from "../../../constants/basics/rightship-dialog.constants";
import * as Enumerable from "linq";
import { DateRangeSelectorMode } from "../date-range-selector/date-range-selector";
import { CheckedBooleanSelectorMode } from "../checked-boolean-selector/checked-boolean-selector";
import { BindingSignaler } from 'aurelia-templating-resources'
import { RightShipMultiLookupModels, MultiLookupModel } from "../multi-checked-list-selector/multi-checked-list-selector";
import { RightshipDataGrid } from "../rightship-data-grid/rightship-data-grid";
import KeyDownEvent = JQuery.KeyDownEvent;
import { LookupValue } from "../../../models/lookups/lookup.models";

const mobileFiltersWidthDelimiter: number = 1024; //Screen width above which mobile filters are hidden

@autoinject()
@transient()
export class MultiFilterList {

    public pageCountSet: any[] = [
        { label: 10, value: 10 },
        { label: 50, value: 50 },
        { label: 100, value: 100 },
    ];

    // Determines whether any filters are currently active
    public hasActiveFilters: boolean = false;

    // Determines whether mobile filters are open or not visible
    public mobileFiltersOpen: boolean;

    // Holds currently selected mobile filter column
    public selectedMobileFilterColumn: DataGridColumn = null;

    @observable()
    public dataGridVM: RightshipDataGrid;

    @bindable
    public itemSource: ItemSource = undefined;

    @bindable
    public filterColumns: DataGridColumn[] = undefined;

    @bindable
    public onRowSelected: (item, dataGridVM) => string;

    @bindable
    public getParams: any;

    @bindable
    public editRoute: any;

    @bindable
    public displayName: string;

    @bindable
    public showClear: boolean;

    @bindable
    public keywords: string;

    @bindable
    public defaultPageCount: number;

    @bindable
    public selectionMode: 'none' | 'single' | 'multiple' = 'single';

    @bindable
    public customFilterView: string;

    //Document click event subscription
    private _subscriptionToDocumentClickEvent: Subscription;

    private _filterSubscriptions: Subscription[] = [];

    private mouseDownInFilter: boolean = false;

    private _initialFilters: QueryFilter[] = [];

    private _multiLookupModels: { [name: string]: MultiLookupModel[]; } = {};

    constructor(private router: Router, private i18N: I18N, private dialogClientService: DialogClientService,
        private eventAggregator: EventAggregator, private bindingEngine: BindingEngine, private bindingSignaler: BindingSignaler) {
    }

    attached() {
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closeOpenFilters);
        this.reloadActiveFilters();
    }

    detached() {
        if (this._subscriptionToDocumentClickEvent) {
            this._subscriptionToDocumentClickEvent.dispose();
        }

        this.disposeFilterSubscriptions();
    }

    adjustFilters = () => {
        if (window.innerWidth >= mobileFiltersWidthDelimiter) {
            this.mobileFiltersOpen = false;
        }
        else {
            this.closeMobileFilters();
        }
    }

    /**
     * Datagrid 'displaying items' text
     */
    public get displayingItemsText() {
        if (this.dataGridVM) {
            const unfilteredCount = this.dataGridVM.unfilteredCount ? this.dataGridVM.unfilteredCount.toString() : '0';
            const currentCount = (this.dataGridVM.page + 1) * this.dataGridVM.pageCount;
            const current = currentCount > Number(unfilteredCount) ? unfilteredCount : currentCount.toString();
            return this.i18N.tr('displaying-items-text').replace("{0}", (this.dataGridVM.page == 0 ? 1 : (this.dataGridVM.page * this.dataGridVM.pageCount) + 1).toString()).replace("{1}", current).replace("{2}", unfilteredCount).replace("{3}", this.displayName);
        }
        return "";
    }

    /**
     * 'itemSource' changed event handler
     */
    public itemSourceChanged() {
        if (this.itemSource) {
            if (this.itemSource.columns) {
                //Select columns which contain filters
                this.itemSource.columns
                    .filter((filterColumn: DataGridColumn) => filterColumn.filters)
                    .forEach((column: DataGridColumn) => column.filters
                        .forEach((filter: QueryFilter) => {
                            filter.originalValue = filter.filterProperty.type != DataType.CustomTemplate ? CloneHelper.cloneObject(column.originalValue) : CloneHelper.cloneObject(filter.filterValue);
                        })
                    );
            }
            //If the item source contains initial filters
            if (this.itemSource.initialFilters) {
                this.itemSource.initialFilters.forEach((filter: QueryFilter) => {
                    filter.originalValue = filter.filterProperty.type != DataType.CustomTemplate ? null : CloneHelper.cloneObject(filter.filterValue);
                });
            }

            this.bindingSignaler.signal('item-source-changed');
        }
        this._initialFilters = this.itemSource?.initialFilters ?? [];
    }

    /**
     * 'dataGrid' item-selected event handler
     * @param event Event
     */
    public dataGridItemSelectedEventHandler(event) {
        if (!event.detail.item) {
            return;
        }
        //since on click the page will be opened, there is no need to set isSelected to true
        if (event?.detail?.row && this.selectionMode == "single")
            event.detail.row.isSelected = false;
        const selectedItem = event.detail.item;
        const route = this.onRowSelected ? this.onRowSelected(selectedItem, this.dataGridVM)
            : this.editRoute ? this.editRoute : null;
        if (typeof route == "string") {
            const params = this.getParams(selectedItem);
            this.router.navigateToRoute(route, params);
        } else if (typeof route == "function") {
            route(selectedItem, this.router);
        }
    }

    /**
     * Desktop filter click
     * @param $event
     * @param column
     */
    desktopFilterClick($event: Event, column: DataGridColumn) {
        if (column?.dataType === DataType.Button) {
            return;
        }

        if ($event) {
            $event.stopPropagation();
        }
    }

    /**
     * Desktop filter container click
     * @param $event
     */
    desktopFilterContainerClick($event) {
        if ($event) {
            $event.stopPropagation();
        }

        this.mouseDownInFilter = false;
    }

    /**
     * 'dataGridVM' changed handler
     */
    dataGridVMChanged() {
        if (this.defaultPageCount) {
            this.setPageCount(this.pageCountSet.filter(c => c.value == this.defaultPageCount)[0]);
        }
    }

    /**
     * 'defaultPageCount' changed handler
     */
    defaultPageCountChanged() {
        if (this.defaultPageCount) {
            this.setPageCount(this.pageCountSet.filter(count => count.value == this.defaultPageCount)[0]);
        }
    }

    /**
     * Set DataGridColumn page count
     * @param pageCount
     */
    setPageCount(pageCount: { label: string, value: number }) {
        if (this.dataGridVM) {
            this.dataGridVM.pageCount = pageCount.value;
        }
    }

    /**
     * 'filterColumns' changed handler
     */
    filterColumnsChanged() {
        this.disposeFilterSubscriptions();

        //Multi-lookup selector column filters
        const columnsWithMultiLookupSelectFilters = this.filterColumns.filter((column: DataGridColumn) => column.dataType == DataType.MultiLookupSelector);

        columnsWithMultiLookupSelectFilters.filter((column: DataGridColumn) => column.dataType == DataType.CustomTemplate)
            .forEach((column: DataGridColumn) => {
                column.parameter.reloadData = this.reloadData.bind(this);
            });

        const columnsWithCustomFilters = Enumerable.from(this.filterColumns).except(columnsWithMultiLookupSelectFilters);
        QueryColumnHelper.addFilteringToColumns(columnsWithMultiLookupSelectFilters);

        columnsWithCustomFilters.forEach((column: DataGridColumn) => {
            if (column.filters?.length > 0
                //if data type is range selector and there are not 2 filtes (>= and  <=) we need to set the appropriate filters for it
                && !(column.dataType == DataType.RangeSelector && column.filters.length < 2)) return;

            switch (column.dataType) {
                case DataType.RangeSelector:
                    column.filters = [];// clear filters to make sure there are only the >= and <= filters
                    column.filters.push((<QueryFilter>{
                        filterOperator: <EntityMetaDataModelOperator>{
                            requiresInterpolation: false,
                            translatedSymbol: "ge",
                            friendlyName: ">=",
                            requiresFilterValue: true
                        },
                        filterProperty: <EntityFilterPropertyModel>{
                            name: column.name,
                            isDynamicProperty: column.isDynamicProperty,
                            lookupMappedToProperty: null,
                            type: this.getType(column)
                        },
                        filterValue: null,
                        isAnd: true,
                        skipOnNullOrEmpty: true
                    }),
                        (<QueryFilter>{
                            filterOperator: <EntityMetaDataModelOperator>{
                                requiresInterpolation: false,
                                translatedSymbol: "le",
                                friendlyName: "<=",
                                requiresFilterValue: true
                            },
                            filterProperty: <EntityFilterPropertyModel>{
                                name: column.name,
                                isDynamicProperty: column.isDynamicProperty,
                                lookupMappedToProperty: null,
                                type: this.getType(column)
                            },
                            filterValue: null,
                            isAnd: true,
                            skipOnNullOrEmpty: true
                        }));
                    break;
                case DataType.CheckedListSelector:
                case DataType.CheckedLookupSelector:
                case DataType.RatingSelector:
                case DataType.GHGRatingSelector:
                    column.filters.push((<QueryFilter>{
                        filterOperator: <EntityMetaDataModelOperator>{
                            translatedSymbol: "{0} in ({1})",
                            friendlyName: "in",
                            requiresFilterValue: true,
                            requiresInterpolation: true,
                        },
                        filterProperty: <EntityFilterPropertyModel>{
                            name: column.name,
                            isDynamicProperty: column.isDynamicProperty,
                            type: this.getType(column),
                            lookupMappedToProperty: column.parameter
                        },
                        filterValue: null,
                        isAnd: true,
                        skipOnNullOrEmpty: true
                    }));
                    break;
            }
        });

        columnsWithMultiLookupSelectFilters
            .filter((column: DataGridColumn) => column.dataType == DataType.Boolean || column.dataType == DataType.CustomTemplate)
            .forEach((column: DataGridColumn) => {
                column.filters.forEach((filter: QueryFilter) => {
                    const sub: Subscription = this.bindingEngine
                        .propertyObserver(filter, "filterValue")
                        .subscribe(($event) => this.reloadData($event, null));
                    this._filterSubscriptions.push(sub);
                });
            });

        if (this.itemSource) {
            this.itemSource.initialFilters = this.getAllFilters();
        }

        if (this.filterColumns)
            this.filterColumns.filter((filterColumn: DataGridColumn) => filterColumn.filters)
                .forEach((filterColumn: DataGridColumn) => filterColumn.filters
                    .forEach((filter: QueryFilter) => {
                        if (filterColumn.dataType == DataType.RangeSelector) {
                            filter.originalValue = filterColumn.filters.indexOf(filter) == 0 ? filterColumn.parameter : filterColumn.secondParameter;
                            //set default value
                            if (!filter.filterValue && filter.filterValue != 0) {
                                filter.filterValue = filter.originalValue;
                            }
                        }
                        else {
                            filter.originalValue = filter.filterProperty.type != DataType.CustomTemplate ? CloneHelper.cloneObject(filterColumn.originalValue) : CloneHelper.cloneObject(filter.filterValue);
                        }
                    })
                );
    }

    public getAllFilters() {
        let filtersToInclude = this._initialFilters;//.splice(0); this was making the advanced search, not working at all.
        this.filterColumns.forEach((x: DataGridColumn) => {
            if (x.dataType == DataType.CheckedListSelector) {
                filtersToInclude = filtersToInclude.concat(this.getCheckedListsFilters(x));
            } else if (x.dataType == DataType.CheckedLookupSelector) {
                filtersToInclude = filtersToInclude.concat(this.getCheckedLookupFilter(x));
            } else if (x.dataType == DataType.RatingSelector) {
                filtersToInclude = filtersToInclude.concat(this.getCheckedLookupFilter(x));
            } else if (x.dataType == DataType.GHGRatingSelector) {
                filtersToInclude = filtersToInclude.concat(this.getCheckedListsFilters(x));
            } else if (x.filtersResolver) {
                const filters = x.filtersResolver(x.filters);
                if (filters) {
                    filtersToInclude = filtersToInclude.concat(filters);
                }
            } else if (x.filters) {
                filtersToInclude = filtersToInclude.concat(x.filters);
            }
        });
        return filtersToInclude;
    }

    reloadData = ($event, column: DataGridColumn) => {
        if ($event?.stopPropagation) {
            $event.preventDefault();
            $event.stopPropagation();
        }
        if (column && $event) {
            if ($event.detail.closeFilter) {
                this.toggleFilterVisibility(null, column);
            } else {
                column.filters.forEach((filter: QueryFilter) => {
                    filter.filterValue = CloneHelper.cloneObject(filter.originalValue);
                });
            }
            if (Object.keys($event.detail).indexOf("appliedFilters") > -1) {
                column.filters = $event.detail.appliedFilters;
            }
        }

        this.reloadActiveFilters();
        this.itemSource.initialFilters = this.getAllFilters();
    }

    public getCheckedListsFilters(x: DataGridColumn): QueryFilter[] {
        const values = <number[]>x.filters[0].filterValue;
        const queries: QueryFilter[] = [];

        if (values) {
            const guid = Guid.generateGuid();
            queries.push((<QueryFilter>{
                filterOperator: <EntityMetaDataModelOperator>{
                    requiresInterpolation: true,
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true
                },
                filterProperty: <EntityFilterPropertyModel>{
                    name: x.name,
                    isDynamicProperty: x.isDynamicProperty,
                    lookupMappedToProperty: x.parameter,
                    type: this.getType(x)
                },
                filterValue: values,
                isAnd: true,
                skipOnNullOrEmpty: true,
                groupColor: guid,
            }));
        }

        return queries;
    }

    public getCheckedLookupFilter(x: DataGridColumn): QueryFilter[] {
        const values = <number[]>x.filters[0].filterValue;
        const queries: QueryFilter[] = [];

        if (values?.length > 0) {
            const string = `${values.toString()}`;
            const guid = Guid.generateGuid();
            queries.push((<QueryFilter>{
                filterOperator: <EntityMetaDataModelOperator>{
                    requiresInterpolation: true,
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true
                },
                filterProperty: <EntityFilterPropertyModel>{
                    name: x.name,
                    isDynamicProperty: x.isDynamicProperty,
                    lookupMappedToProperty: x.parameter,
                    type: this.getType(x)
                },
                filterValue: string,
                isAnd: true,
                skipOnNullOrEmpty: true,
                groupColor: guid
            }))
        }

        return queries;
    }

    public clearFilters() {
        this.filterColumns.forEach((column: DataGridColumn) => {
            column.filters.forEach((filter: QueryFilter) => {
                filter.filterValue = CloneHelper.cloneObject(filter.originalValue);
            })

            if (column.dataType == DataType.RangeSelector) {
                column.isApplied = false;
            }

            if (column.dataType == DataType.Many2Many) {
                column.value = [];
                column.filters = [];
            }

            column.fieldTitle = column.mobileTitle = column.title;
            column.subText = "";

            if (column.dataType == DataType.MultiLookupSelector && column.parameter && this._multiLookupModels && this._multiLookupModels[column.parameter]) {
                this._multiLookupModels[column.parameter].forEach(z => (z.filteredValues || []).forEach(x => {
                    x["isSelected"] = false;
                    x["isParentTicked"] = false;
                }))
            }
        });

        this.reloadActiveFilters();
        this.dataGridVM.clearFilters();
        this.reloadData(null, null);
    }

    public clearFilter(event, column: DataGridColumn) {
        if (event) {
            event.stopPropagation();
        }
        column.filters.forEach((filter: QueryFilter) => {
            filter.filterValue = CloneHelper.cloneObject(filter.originalValue);
        });

        if (column.parameter && this._multiLookupModels && this._multiLookupModels[column.parameter]) {
            this._multiLookupModels[column.parameter]
                .forEach(z => (z.filteredValues || [])
                .forEach(x => {
                    x.isSelected = false;
                    x.isParentTicked = false;
                }));
        }

        column.mobileDescription = "";

        if (column.dataType == DataType.RangeSelector) {
            column.isApplied = false;
        }

        if (column.dataType == DataType.Many2Many) {
            column.value = [];
            column.filters = [];
        }

        column.fieldTitle = column.mobileTitle = column.title;
        column.subText = "";

        this.reloadActiveFilters();
        this.reloadData(null, null);
    }

    /**
     * Return corresponding filter html content
     * @param column Columnm
     * @param isMobile Is filter for mobile
     */
    public filtersHtml(column: DataGridColumn, isMobile: boolean = false): string {
        if (!column) return "";

        if (column.dataType == DataType.Date || column.dataType == DataType.DateTime) {
            const mode: DateRangeSelectorMode = isMobile ? 'mobile-filter' : 'default';
            return `<date-range-selector column.bind="column" mode="${mode}" apply-filters.delegate="reloadData($event, column)"></date-range-selector>`;
        }

        if (column.dataType == DataType.Boolean) {
            const mode: CheckedBooleanSelectorMode = isMobile ? 'mobile-filter' : 'default';
            return `<checked-boolean-selector column.bind="column" source="${this.itemSource ? this.itemSource.source : ""}" apply-filters.delegate="reloadData($event, column)"
                mode="${mode}" mobile-description.from-view="column.mobileDescription"></checked-boolean-selector>`
        }
        if (column.dataType == DataType.CheckedListSelector) {
            return `<checked-list-selector ${column.secondParameter && Array.isArray(column.secondParameter) && column.secondParameter.length > 0 ? `items.bind="column.secondParameter"` : `uri.to-view="column.filters[0].filterProperty.lookupMappedToProperty"`} selected-item-ids.two-way="column.filters[0].filterValue"
                mobile-title.from-view="column.mobileTitle" mobile-description.from-view="column.mobileDescription" apply-filters.delegate="reloadData($event, column)"
                property-name="${column.title}" external-label.from-view="column.fieldTitle" reload-active-filters.delegate="reloadActiveFilters()"></checked-list-selector>`;
        }
        if (column.dataType == DataType.RatingSelector) {
            const min = column.parameter ? column.parameter : 0;
            const max = column.secondParameter ? column.secondParameter : 5;
            const showCommas = column.thirdParameter;
            return `<rating-selector property-name="${column.title}" selected-item-ids.two-way="column.filters[0].filterValue" max="${max}" min="${min}" reload-active-filters.delegate="reloadActiveFilters()"
                mobile-title.from-view="column.mobileTitle" mobile-description.from-view="column.mobileDescription" external-label.from-view="column.fieldTitle" show-commas="${showCommas}"
                apply-filters.delegate="reloadData($event, column)" end-label.bind="column.placeholder"
                start-external-label="${column.title}" all-selected-label="default">
                </rating-selector>`;
        }
        if (column.dataType == DataType.GHGRatingSelector) {
            if (column.filters?.length > 0 && Object.keys(column.filters[0]).indexOf("ghgSelectedItems") > -1) {
                column.ghgSelectedItems = column.filters[0]["ghgSelectedItems"];
            }
            return `<ghg-rating-selector property-name="${column.title}" selected-rating-weights.two-way="column.filters[0].filterValue"
                selected-items.two-way="column.ghgSelectedItems"
                mobile-title.from-view="column.mobileTitle" mobile-description.from-view="column.mobileDescription" external-label.from-view="column.fieldTitle"
                apply-filters.delegate="reloadData($event, column)" start-external-label="${column.title}"></ghg-rating-selector>`;
        }
        if (column.dataType == DataType.RangeSelector) {
            const min = column.parameter ? column.parameter : 0;
            const max = column.secondParameter ? column.secondParameter : 100;
            let showCommas = column.thirdParameter;
            let step = 1;
            if (typeof column.thirdParameter === "object") {
                const additionalData = column && column.thirdParameter ? column.thirdParameter : null;
                showCommas = additionalData && additionalData.showCommas;
                step = additionalData && additionalData.step ? additionalData.step : 1;
            }
            column.isApplied = column.filterActive || (column.filterActive == undefined && column.filters && column.filters.length >= 2 && (column.filters[0].filterValue > min || column.filters[1].filterValue < max));
            return `<range-selector property-name="${column.title}" first-value.two-way="column.filters[0].filterValue" max="${max}" min="${min}" reload-active-filters.delegate="reloadActiveFilters()"
                mobile-title.from-view="column.mobileTitle" mobile-description.from-view="column.mobileDescription" external-label.from-view="column.fieldTitle" show-commas="${showCommas}"
                second-value.two-way="column.filters[1].filterValue" apply-filters.delegate="reloadData($event, column)" end-label.bind="column.placeholder" is-applied.two-way="column.isApplied"
                step.bind="${step}"></range-selector>`;
        }
        if (column.dataType == DataType.CheckedLookupSelector) {
            return `<checked-lookup-selector uri.to-view="column.filters[0].filterProperty.lookupMappedToProperty" selected-item-ids.two-way="column.filters[0].filterValue"
                apply-filters.delegate="reloadData($event, column)" property-name="${column.title}"  mobile-title.from-view="column.mobileTitle" reload-active-filters.delegate="reloadActiveFilters()"
                mobile-description.from-view="column.mobileDescription" external-label.from-view="column.fieldTitle"></checked-lookup-selector>`
        }
        if (column.dataType == DataType.Button) {
            return `<custom-button>${column.parameter ? column.parameter() : null}</custom-button>`
        }
        if (column.dataType == DataType.Default) {
            column.thirdParameter = column.thirdParameter ? column.thirdParameter :
                ($event) => {
                    if ($event) {
                        const e: KeyDownEvent = $event.detail;

                        if (e.key == "Enter") {
                            $event.detail.closeFilter = true;
                            this.reloadData($event, column)
                        }
                    }
                };
            return `<text-box type='text' key-down.delegate='column.thirdParameter($event) & debounce:600' value.two-way='column.filters[0].filterValue & debounce:350'></text-box>`;
        }
        if (column.dataType == DataType.Many2Many) {
            if ((!column.items || column.items.length == 0) && column.parameter && column.parameter instanceof Function) {
                column.items = column.parameter();
            }
            else if (!column.items || column.items.length == 0) column.items = column.parameter;

            return `<many-to-many-filter-selector property-name="${column.name}" selected-ids.two-way="column.value" items.two-way="column.items"
                apply-filters.delegate="reloadData($event, column)" mobile-description.from-view="column.mobileDescription"></many-to-many-filter-selector>`;
        }
        return GridUtils.filtersHtml(column);
    }

    /**
     * Get column data type
     * @param column Column
     */
    public getType(column: DataGridColumn) {
        if (column.dataType == DataType.RangeSelector) {
            return DataType.Number;
        }
        if (column.dataType == DataType.CheckedListSelector || column.dataType == DataType.CheckedLookupSelector) {
            // If has secondParameter then is custom list and not lookup
            if (!column.secondParameter || !Array.isArray(column.secondParameter) || column.secondParameter.length == 0)
                return DataType.Lookup
            // If not lookup check if third paramater is set (should have the datatype), if not set data type as default
            else if (column.thirdParameter) return column.thirdParameter;
            else return DataType.Default;
        }
        if (column.dataType == DataType.GHGRatingSelector) {
            return DataType.Number;
        }
        return column.dataType;
    }

    public toggleFilterVisibility(event, column: DataGridColumn) {
        this.mouseDownInFilter = true;

        if (event) {
            if (event.defaultPrevented) return;

            event.preventDefault();
            event.stopPropagation();
        }

        if (column.dataType === DataType.Button) {
            column.filterOpen = false;
            column.value(null, null, column, event.detail);
        } else {
            column.filterOpen = !column.filterOpen;
        }

        if (column?.filterOpen) {
            if (column.filters)
                column.filters.forEach((filter: QueryFilter) => filter.previousValue = CloneHelper.cloneObject(filter.filterValue));
            column.previousLabel = CloneHelper.cloneObject(column.fieldTitle)
        }

        const filtersToHide = this.filterColumns.filter(x => x != column && x.dataType !== DataType.Button);
        filtersToHide.forEach((column: DataGridColumn) => {
            column.filterOpen = false;
        });
    }

    reloadActiveFilters = () => {
        this.hasActiveFilters = false;
        if (!this.filterColumns) return;

        this.filterColumns.forEach((column: DataGridColumn) => {
            if (!column.filterActive && (column.dataType == DataType.Date || column.dataType == DataType.DateTime
                || column.dataType == DataType.Boolean)) {
                column.filters = [];
                return;
            }

            column.filterActive = false;

            if (column.dataType == DataType.Many2Many) {
                column.filterActive = column.filters && column.filters.length > 0;
                return;
            }
            column.filters.forEach(filter => {
                const value = filter.filterValue;
                if (column.dataType == DataType.RangeSelector) {
                    if (value && value != null) {
                        column.filterActive = (column.isApplied || column.isAppliedMobile) || (column.filterActive && (column.filters.findIndex(x => column.parameter == x.filterValue) + column.filters.findIndex(x => column.secondParameter == x.filterValue)) <= 0);
                    }
                } else if (column.dataType == DataType.Boolean) {
                    column.filterActive = value != null;
                } else {
                    const isActive = (value && value != null && ((Array.isArray(value) && value.length > 0) || !Array.isArray(value)));
                    column.filterActive = column.dataType == DataType.MultiLookupSelector ? (column.filterActive || isActive) : isActive;
                }
            })
        });

        this.hasActiveFilters = this.filterColumns?.length > 0 && this.filterColumns.filter(x => x.filterActive == true).length > 0;
    }

    /**
     * Stop event propagation
     * @param event Event
     */
    stopPropagation(event) {
        event.stopPropagation();
    }

    /**
     * Close any open filters
     * @param event Event
     */
    closeOpenFilters = (event: {source: string}) => {
        //If the event source is from the document
        if (event?.source == 'document' && this.filterColumns && !this.mouseDownInFilter) {
            //Select filters that are open
            this.filterColumns.filter((filter: DataGridColumn) => filter.filterOpen)
                //For each of the filter's columns
                .forEach((column: DataGridColumn) => {
                    if (column.filters) {
                        column.filters.forEach((x: QueryFilter) => {
                            x.filterValue = x.previousValue;
                        });
                    }
                    column.fieldTitle = column.previousLabel;
                    if (column.dataType !== DataType.Button) {
                        column.filterOpen = false;
                    }
                });

            this.reloadActiveFilters();
        }

        this.mouseDownInFilter = false;
    }

    /**
     * Dispose filter subscriptions
     */
    disposeFilterSubscriptions() {
        if (this._filterSubscriptions?.length > 0) {
            this._filterSubscriptions.forEach((subscription: Subscription) => subscription.dispose());
        }
    }

    /**
     * Open multi-checked-list-dialog
     * @param column Column
     */
    openMultiCheckListDialog = (column: DataGridColumn) => {
        let cachedMultiLookups = this._multiLookupModels[column.parameter];

        cachedMultiLookups = cachedMultiLookups ? cachedMultiLookups : RightShipMultiLookupModels.getMultiLookupModel(column.parameter);

        let filterValues: { [filterProperty: string]: number[] } = null;
        if (column?.filters.length > 0) {
            filterValues = {};
            column.filters.forEach((x: QueryFilter) => {
                filterValues[x.filterProperty.name] = x.filterValue;
            });
        }

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.MultiCheckedListDialog, {
            isSingleSelect: false,
            multiLookups: cachedMultiLookups,
            title: column.title,
            selectedIds: filterValues,
            onApply: (multiLookups: MultiLookupModel[]) => {
                const column = this.filterColumns.filter((column: DataGridColumn) => column.name == multiLookups.filter((multiLookup: MultiLookupModel) => multiLookup.isMain)[0].propertyName)[0];
                column.filters = [];

                let selectedItems = 0
                let subText = '';
                let subTextCount = 0;

                multiLookups.forEach((multiLookup: MultiLookupModel) => {

                    if (multiLookup.values?.filter((lookupValue: LookupValue) => (<any>lookupValue).isSelected).length) {
                        const selectedValues = multiLookup.values.filter((lookupValue: LookupValue) => (<any>lookupValue).isSelected && !(<any>lookupValue).isParentTicked);
                        selectedValues.forEach((lookupValue: LookupValue) => {
                            if (subTextCount > 0 && subTextCount < 2) {
                                subText += ', ';
                            }

                            if (subTextCount < 2) {
                                subText += lookupValue.name;
                            }

                            subTextCount++;
                        });

                        const selectedIds = selectedValues.map(x => x.id);
                        const filter = QueryFilter.getQuery(multiLookup.propertyName, selectedIds, '{0} in ({1})', DataType.Number, (!column.filters || column.filters.length == 0), false, column.name + '_multi');

                        filter.filterOperator.requiresInterpolation = true;
                        column.filters.push(filter);
                        selectedItems += selectedIds.length;
                    }

                    multiLookup.isShowing = false;
                });

                if (subTextCount > 2) {
                    subText += " +" + (subTextCount - 2) + " more";
                }

                column.subText = subText;
                multiLookups[0].isShowing = true;

                if (selectedItems) {
                    column.fieldTitle = column.mobileTitle = column.title + ' (' + selectedItems + ')';
                    column.filterActive = true;
                }
                else {
                    column.fieldTitle = column.mobileTitle = column.title;
                    column.subText = "";
                    column.filterActive = false;
                }

                this._multiLookupModels[column.parameter] = multiLookups;

                this.reloadActiveFilters();
                this.itemSource.initialFilters = this.getAllFilters();
            }
        }, true, false, false, 'right');
    }

    /**
     * Open mobile filters
     */
    public openMobileFilters() {
        //removing filterValue subscriptions since the results on mobile will load when show results is clicked
        this.disposeFilterSubscriptions();

        this.mobileFiltersOpen = true;
    }

    /**
     * Clear all mobile filters
     */
    mobileClearAllFilters() {
        this.filterColumns.forEach((column: DataGridColumn) => {
            column.isAppliedMobile = false;
        });

        this.clearFilters()
    }

    /**
     * Filter clicked handler
     * @param column Column
     */
    mobileFilterClickedHandler(column: DataGridColumn) {
        switch (column.dataType) {
            case DataType.Button:
                column.value(null, null, column);
                break;
            default:
                this.selectedMobileFilterColumn = column;
                break;
        }
    }

    /**
     * Clear selected filter column
     */
    mobileClearSelectedFilterColumn() {
        if (this.selectedMobileFilterColumn) {
            if (this.selectedMobileFilterColumn.dataType == DataType.Date || this.selectedMobileFilterColumn.dataType == DataType.DateTime) {
                this.selectedMobileFilterColumn.filters = [];
                return;
            }

            this.selectedMobileFilterColumn.filters.forEach((queryFilter: QueryFilter) => {
                queryFilter.filterValue = CloneHelper.cloneObject(queryFilter.originalValue);
            });

            this.selectedMobileFilterColumn.isAppliedMobile = false;

            if (this.selectedMobileFilterColumn.dataType == DataType.RangeSelector) {
                this.selectedMobileFilterColumn.isApplied = false;
            }
        }
    }

    /**
     * Close any open mobile filter
     */
    closeSelectedMobileFilters() {
        //if a filter is selected, it should not close the dialog on apply or back
        if (this.selectedMobileFilterColumn != null) {
            //If the selected filter was not applied (Select button was not pressed)
            if(!this.selectedMobileFilterColumn.isAppliedMobile) {
                //Clear any filter changes
                this.mobileClearSelectedFilterColumn();
            }

            this.selectedMobileFilterColumn = null;
        }
    }

    /**
     * Close mobile filters
     */
    closeMobileFilters() {
        this.closeSelectedMobileFilters();

        this.mobileFiltersOpen = false;
    }

    /**
     * Select applied mobile filter
     */
    mobileApplySelectedFilterColumn() {
        this.reloadData(null, null);

        //If a mobile filter is selected
        if(this.selectedMobileFilterColumn != null) {
            this.selectedMobileFilterColumn.isAppliedMobile = true;

            if(!this.selectedMobileFilterColumn.isAppliedMobile) this.clearSelectedMobileFilterColumn();
            this.selectedMobileFilterColumn = null;
        }
    }

    clearSelectedMobileFilterColumn() {
        if (this.selectedMobileFilterColumn) {
            if (this.selectedMobileFilterColumn.dataType == DataType.Date || this.selectedMobileFilterColumn.dataType == DataType.DateTime) {
                this.selectedMobileFilterColumn.filters = [];
                return;
            }

            this.selectedMobileFilterColumn.filters.forEach(x => {
                x.filterValue = CloneHelper.cloneObject(x.originalValue);
            });

            this.selectedMobileFilterColumn.isAppliedMobile = false;

            if(this.selectedMobileFilterColumn.dataType == DataType.RangeSelector)
            {
                this.selectedMobileFilterColumn.isApplied = false;
            }
        }
    }

    /**
     * Mobile filters 'apply' button click
     */
    mobileApplyFilters() {
        this.reloadData(null, null);

        this.closeMobileFilters();
    }
}